import React from 'react';
import { Session as TalkJsSession } from '@talkjs/react';
import { Box, CircularProgress } from '@mui/material';
import Intercom from '@intercom/messenger-js-sdk';
import { Outlet, useLocation } from 'react-router-dom';
import Talk from 'talkjs';
import TALK_APP_ID from '../../talkconfig';
import useUser from '../../hooks/global/useUser';
import SideNav from '../navigation/SideNav';
import PrimaryNav from '../navigation/PrimaryNav';
import Login from '../auth/Login';
import FullPageModal from '../modal/FullPageModal';
import CheckoutForm from '../form/CheckoutForm';
import ConfirmationModal from '../modal/ConfirmationModal';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import { AppContext } from '../../context/AppContext/context';
import LoadingModal from './LoadingModal';
import useFreeTrialStatus from '../../hooks/subscriptions/useFreeTrialStatus';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import SubscriptionModalPopup from '../subscriptions/SubscriptionModalPopup';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';
import CustomerAppointmentSearch from '../appointments/CustomerAppointmentSearch';
import { resetConversations } from '../../services/chats';
import { ROUTES } from '../../constants/global';
import FormSmallModal from './FormSmallModal';
import SplitPaymentsGroupOverview from '../payments/SplitPaymentsGroupOverview';
import useSelectedSplitPaymentsGroup from '../../hooks/checkouts/useSelectedSplitPaymentsGroup';
import {
  formatDollarAmount,
  isReactNativeWebView,
} from '../../utils/global/helper';

function DashboardLayout({
  primaryNav = true,
  sideNav = true,
  noUser,
}: {
  primaryNav?: boolean;
  sideNav?: boolean;
  noUser?: boolean;
} = {}) {
  const location = useLocation();
  const { state, dispatch } = React.useContext(AppContext);
  const { checkout, confirmationModal, loadingModal, awaitedFormModal } = state;
  const { selectedSplitPaymentsGroupId, setSelectedSplitPaymentsGroupId } =
    useSelectedSplitPaymentsGroup();

  const { user: loadedUser, isLoading } = useUser();
  const { isBannerShown } = useFreeTrialStatus();
  const { data: subscription, isLoading: isLoadingSub } = useGetSubscription();

  const { isLoading: isLoadingOnboarding, isEnabled: isOnboardingEnabled } =
    useGetOnboarding();

  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const { closeCheckout, closeTerminalCheckoutModal, openCheckout } =
    useCheckoutForm();

  const classes = React.useMemo(() => {
    const list: string[] = [];
    if (sideNav) {
      list.push('content-container');
      if (isBannerShown) list.push('banner-above');
    }
    return list;
  }, [sideNav, isBannerShown]);

  const syncUser = React.useCallback(
    () =>
      new Talk.User({
        id: loadedUser?.userId || '',
        name: loadedUser?.firstName || '',
        email: loadedUser?.email || '',
        phone: loadedUser?.phoneNumber || '',
        role: 'artist',
      }),
    [
      loadedUser?.email,
      loadedUser?.firstName,
      loadedUser?.phoneNumber,
      loadedUser?.userId,
    ],
  );

  const onNewChatMessage = React.useCallback(() => {
    const isInChat = location.pathname.startsWith(ROUTES.chat);
    if (isInChat) return;
    resetConversations();
  }, [location.pathname]);

  const onUnreadsChange = React.useCallback(
    (unreadConversations: Talk.UnreadConversation[]) => {
      const unreadData = unreadConversations.reduce(
        (acc, conversation) => {
          const customAttributes = conversation?.conversation?.custom;
          const isMuted =
            loadedUser?.userId &&
            customAttributes?.[loadedUser?.userId] === 'muted';
          const { lastMessage } = conversation;
          let thisCount = isMuted
            ? 0
            : conversation.unreadMessageCount || (lastMessage?.read ? 0 : 1);

          // message is SystemMessage
          const subtractLastMessage =
            lastMessage &&
            !lastMessage.read &&
            lastMessage.origin === 'rest' &&
            !lastMessage.senderId;
          if (subtractLastMessage && thisCount > 0) {
            thisCount -= 1;
          }

          if (thisCount <= 0) {
            return acc;
          }

          acc[conversation.conversation.id] = thisCount;

          return acc;
        },
        {} as Record<string, number>,
      );
      dispatch({ type: 'SET_UNREAD_CONVERSATIONS', payload: unreadData });
    },
    [dispatch, loadedUser?.userId],
  );

  const { Session, props: sessionProps } = React.useMemo<
    | {
        Session: typeof TalkJsSession;
        props: React.ComponentProps<typeof TalkJsSession>;
      }
    | {
        Session: React.FC;
        props: {};
      }
  >(
    () =>
      loadedUser?.business?.id
        ? {
            Session: TalkJsSession,
            props: {
              appId: TALK_APP_ID,
              syncUser,
              onMessage: onNewChatMessage,
              onUnreadsChange,
            },
          }
        : { Session: React.Fragment, props: {} },
    [loadedUser?.business?.id, onNewChatMessage, onUnreadsChange, syncUser],
  );

  if (
    isLoading ||
    (!!loadedUser?.employee?.paymentCustomerId && isLoadingSub) ||
    (isOnboardingEnabled && isLoadingOnboarding) ||
    // Require auth token from native app
    (!loadedUser && isReactNativeWebView())
  )
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CircularProgress color="primary" />
      </Box>
    );

  if (!loadedUser?.userId) return <Login />;

  const planPrice = subscription?.subscription?.plan.price
    ? Math.round(subscription.subscription.plan.price / 100)
    : 0;

  const planName = `${loadedUser?.business?.type}: ${subscription?.subscription?.plan.name} - ${subscription?.subscription?.status}`;

  Intercom({
    app_id: 'nchtk2tv',
    user_id: loadedUser?.userId,
    name: loadedUser?.firstName,
    email: loadedUser?.email,
    created_at: Math.floor(new Date(loadedUser?.createdAt).getTime() / 1000),
    company: {
      id: loadedUser?.business?.id,
      name: loadedUser?.business?.name,
      plan: planName,
      monthly_spend: planPrice,
    },
  });

  const user = noUser ? null : loadedUser;
  const isConfirmationModalOpen = confirmationModal.status === 'awaiting';
  const isAwaitedFormModalOpen =
    awaitedFormModal.status === 'awaiting' && !!awaitedFormModal.form;
  const isLoadingModalOpen = loadingModal.status === 'loading';

  const setCheckoutModalOpen = (open: boolean) =>
    open ? openCheckout() : closeCheckout();

  return (
    // @ts-expect-error - This is a valid component
    <Session {...sessionProps}>
      <div className={classes.join(' ')}>
        {primaryNav && (
          <PrimaryNav
            loadedUser={user ?? undefined}
            mobileDrawerOpen={mobileDrawerOpen}
            setMobileDrawerOpen={setMobileDrawerOpen}
          />
        )}
        {sideNav && (
          <SideNav
            mobileDrawerOpen={mobileDrawerOpen}
            setMobileDrawerOpen={setMobileDrawerOpen}
            loadedUser={user ?? undefined}
          />
        )}
        <Outlet />
      </div>
      <SubscriptionModalPopup />
      <FullPageModal
        modalOpen={checkout.checkoutModalOpen}
        setModalOpen={setCheckoutModalOpen}
        handleClose={closeCheckout}
        name="Checkout">
        <CheckoutForm />
      </FullPageModal>
      <ConfirmationModal
        open={!isConfirmationModalOpen && checkout.terminalCheckoutModal.isOpen}
        handleClose={closeTerminalCheckoutModal}
        title={checkout.terminalCheckoutModal.title}
        message={checkout.terminalCheckoutModal.message}
        positiveAction={closeTerminalCheckoutModal}
        positiveText={checkout.terminalCheckoutModal.buttonText}
        isLoading={checkout.terminalCheckoutModal.status === 'loading'}
        isSuccess={checkout.terminalCheckoutModal.status === 'success'}
        hideButtons={checkout.terminalCheckoutModal.status === 'success'}
        isDismissable={
          checkout.terminalCheckoutModal.status !== 'loading' &&
          checkout.terminalCheckoutModal.status !== 'success'
        }
      />

      <ConfirmationModal
        open={isConfirmationModalOpen}
        handleClose={() => confirmationModal.resolve?.(false)}
        negativeAction={() => confirmationModal.resolve?.(false)}
        positiveAction={() => confirmationModal.resolve?.(true)}
        message={confirmationModal.message}
        positiveText={confirmationModal.positiveText || 'Continue'}
        negativeText={confirmationModal.negativeText}
        isDismissable={confirmationModal.dismissible}
        title={confirmationModal.title}
        isSmall={confirmationModal.isSmall}
        hideCloseButton={confirmationModal.hideCloseButton}
        noWrapButtonsText={confirmationModal.noWrapButtonsText}
      />

      {isAwaitedFormModalOpen && (
        <FormSmallModal
          open={isAwaitedFormModalOpen}
          onClose={() => awaitedFormModal.resolve?.(null)}
          {...awaitedFormModal}
        />
      )}

      <LoadingModal
        header={loadingModal.header}
        message={loadingModal.message}
        open={isLoadingModalOpen}
      />

      <SplitPaymentsGroupOverview
        splitPaymentsGroupId={selectedSplitPaymentsGroupId}
        onClose={() => setSelectedSplitPaymentsGroupId(null)}
      />

      <CustomerAppointmentSearch />
    </Session>
  );
}

export default DashboardLayout;
